import { Fund, FundPortfolio } from "creso-core/types";
import { AmcLogo } from "@core/components/logo/amc-logo";
import { Pie, PieChart } from "recharts";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  getPieMinAngle,
} from "web-design-system";
import { formatAsPercentage } from "creso-core/utils";
import { assetAllocationRowConfig } from "./config";

export function AssetAllocationCell({
  label,
  fund,
  portfolio,
  variant = "default",
}: {
  label: string;
  fund?: Fund;
  portfolio: FundPortfolio;
  variant?: "default" | "report";
}) {
  const accessor = assetAllocationRowConfig.find(
    (config) => config.label === label
  )?.accessor;

  if (accessor === "legal_name") {
    return (
      <div className="flex items-center gap-2">
        <AmcLogo amc_code={fund?.provider_company_id ?? null} />
        <span className="text-sm">{fund?.fund_name ?? ""}</span>
      </div>
    );
  }

  if (accessor === "asset_allocation") {
    return <AssetAllocationChart portfolio={portfolio} variant={variant} />;
  }

  if (accessor === "aggregated_allocation") {
    return (
      <AggregatedAllocationChart portfolio={portfolio} variant={variant} />
    );
  }
}

function AssetAllocationChart({
  portfolio,
  variant = "default",
}: {
  portfolio?: FundPortfolio;
  variant?: "default" | "report";
}) {
  if (!portfolio) {
    return null;
  }

  const otherAssetAllocation =
    100 -
    (parseFloat(portfolio.asset_alloc_equity_net ?? "0") +
      parseFloat(portfolio.asset_alloc_bond_net ?? "0") +
      parseFloat(portfolio.asset_alloc_cash_net ?? "0"));

  // Prepare data for recharts
  const assetAllocationData = [
    {
      category: "Equity",
      amount: parseFloat(portfolio.asset_alloc_equity_net ?? "0"),
      fill: "var(--color-chart-1)",
    },
    {
      category: "Bond",
      amount: parseFloat(portfolio.asset_alloc_bond_net ?? "0"),
      fill: "var(--color-chart-2)",
    },
    {
      category: "Cash",
      amount: parseFloat(portfolio.asset_alloc_cash_net ?? "0"),
      fill: "var(--color-chart-3)",
    },
    {
      category: "Others",
      amount: otherAssetAllocation,
      fill: "var(--color-chart-4)",
    },
  ];

  const assetAllocationChartConfig = {
    amount: {
      label: "Asset Allocation (%)",
    },
    Equity: {
      label: "Equity",
      color: "var(--color-chart-1)",
    },
    Bond: {
      label: "Bond",
      color: "var(--color-chart-2)",
    },
    Cash: {
      label: "Cash",
      color: "var(--color-chart-3)",
    },
    Others: {
      label: "Others",
      color: "var(--color-chart-4)",
    },
  } satisfies ChartConfig;

  return (
    <ChartContainer
      config={assetAllocationChartConfig}
      className="w-full relative aspect-square h-[220px]"
    >
      <PieChart>
        <Pie
          isAnimationActive={variant !== "report"}
          legendType="circle"
          paddingAngle={2}
          cornerRadius={4}
          minAngle={getPieMinAngle(50, 80)}
          nameKey="category"
          dataKey="amount"
          data={assetAllocationData}
          valueKey="amount"
          cx="50%"
          cy="50%"
          label={
            variant === "report"
              ? ({ value }) => formatAsPercentage(value, "-", 1)
              : undefined
          }
          innerRadius={50}
          outerRadius={80}
        />
        {variant !== "report" && (
          <ChartTooltip
            content={
              <ChartTooltipContent
                valueFormatter={(value) =>
                  formatAsPercentage(value as string, "-", 1)
                }
              />
            }
          />
        )}
      </PieChart>
    </ChartContainer>
  );
}

export function AggregatedAllocationChart({
  portfolio,
  variant = "default",
}: {
  portfolio?: FundPortfolio;
  variant?: "default" | "report";
}) {
  if (!portfolio) {
    return null;
  }

  const otherAggregateAllocation =
    100 -
    (parseFloat(portfolio.india_large_cap_net ?? "0") +
      parseFloat(portfolio.india_mid_cap_net ?? "0") +
      parseFloat(portfolio.india_small_cap_net ?? "0"));

  const portfolioAggregatesData = [
    {
      category: "Large Cap",
      amount: parseFloat(portfolio.india_large_cap_net ?? "0"),
      fill: "var(--color-chart-1)",
    },
    {
      category: "Mid Cap",
      amount: parseFloat(portfolio.india_mid_cap_net ?? "0"),
      fill: "var(--color-chart-2)",
    },
    {
      category: "Small Cap",
      amount: parseFloat(portfolio.india_small_cap_net ?? "0"),
      fill: "var(--color-chart-3)",
    },
    {
      category: "Others",
      amount: otherAggregateAllocation,
      fill: "var(--color-chart-4)",
    },
  ];

  const assetAllocationChartConfig = {
    amount: {
      label: "Aggregated Allocation (%)",
    },
    "Large Cap": {
      label: "Large Cap",
      color: "var(--color-chart-1)",
    },
    "Mid Cap": {
      label: "Mid Cap",
      color: "var(--color-chart-2)",
    },
    "Small Cap": {
      label: "Small Cap",
      color: "var(--color-chart-3)",
    },
    Others: {
      label: "Others",
      color: "var(--color-chart-4)",
    },
  } satisfies ChartConfig;

  return (
    <ChartContainer
      config={assetAllocationChartConfig}
      className="w-full relative aspect-square h-[220px]"
    >
      <PieChart>
        <Pie
          isAnimationActive={variant !== "report"}
          data={portfolioAggregatesData}
          dataKey="amount"
          nameKey="category"
          cx="50%"
          cy="50%"
          paddingAngle={2}
          cornerRadius={4}
          minAngle={getPieMinAngle(50, 80)}
          innerRadius={50}
          outerRadius={80}
          label={
            variant === "report"
              ? ({ value }) => formatAsPercentage(value, "-", 1)
              : undefined
          }
        />
        {variant !== "report" && (
          <ChartTooltip
            content={
              <ChartTooltipContent
                valueFormatter={(value) =>
                  formatAsPercentage(value as string, "-", 1)
                }
              />
            }
          />
        )}
      </PieChart>
    </ChartContainer>
  );
}
